import type { VehicleDetection } from "../client-messages/vehicles";
import type { VehicleClass } from "../constants";
import type { TimelineEntry } from "../types";
import { cleanDescription } from "./clean-description";
import { formatTravelDirection } from "./format-travel-direction";
import { formatVehicleType } from "./format-vehicle-type";
import { vehicleClass } from "./vehicle-class";

/** A camera image of the vehicle, one per sighting that has one. */
export interface VehicleLogShot {
  readonly key: string;
  readonly imageUrl: string;
}

/** What one row of the vehicle log shows: a vehicle, made from one or more sightings. */
export interface VehicleLogRow {
  /** Stable React key for the row. */
  readonly key: string;
  /**
   * `firstSeenAt` of the row's first sighting in API order, as an ISO timestamp.
   * The API sorts by `first_seen_at` and a group is placed where its first
   * sighting appears, so this is the time the row is ordered by: the newest
   * sighting when sorting newest-first, the oldest when oldest-first.
   */
  readonly time: string;
  readonly shots: readonly VehicleLogShot[];
  /** Distinct plates read across the sightings, in order; empty when none was read. */
  readonly plates: readonly string[];
  /** Distinct direction labels ("Entry", "Exit", "Unknown") across the sightings, in order; empty when none reported one. */
  readonly directions: readonly string[];
  readonly vehicleClass: VehicleClass | null;
  /** Body type label, e.g. "Tipper". */
  readonly vehicleType: string | null;
  readonly description: string | null;
  /** The sightings the row is made from, in the order the API returned them. */
  readonly sightings: readonly VehicleDetection[];
}

/** Maps a timeline entry (single sighting, plate cluster or vehicle group) to one vehicle log row. */
export function toVehicleLogRow(entry: TimelineEntry): VehicleLogRow {
  const sightings = sightingsOf(entry);
  // Like the old timeline cards: "unknown" in any case, or a blank type, is no type.
  const typed = sightings.find((sighting) => isKnown(sighting.vehicleType));
  const description = sightings.map((sighting) => cleanDescription(sighting.appearanceDescription)).find(isKnown);

  return {
    key: rowKey(entry),
    time: sightings[0]?.firstSeenAt ?? "",
    shots: sightings.flatMap((sighting) => {
      const imageUrl = sighting.imageUrls[0];
      return imageUrl === undefined ? [] : [{ key: sightingKey(sighting), imageUrl }];
    }),
    plates: [...new Set(sightings.map((sighting) => sighting.plateText).filter(isKnown))],
    directions: [
      ...new Set(
        sightings.map((sighting) => formatTravelDirection(sighting.travelDirection)).filter((label) => label !== null),
      ),
    ],
    vehicleClass: typed === undefined ? null : vehicleClass(typed.vehicleType),
    vehicleType: typed === undefined ? null : formatVehicleType(typed.vehicleType),
    description: description ?? describeAppearance(sightings),
    sightings,
  };
}

function sightingsOf(entry: TimelineEntry): readonly VehicleDetection[] {
  switch (entry.kind) {
    case "single":
      return [entry.vehicle];
    case "cluster":
      return entry.vehicles;
    case "group":
      return entry.group.members;
  }
}

function rowKey(entry: TimelineEntry): string {
  switch (entry.kind) {
    case "single":
      return sightingKey(entry.vehicle);
    case "cluster":
      return `cluster-${entry.plate}-${entry.vehicles[0]?.firstSeenAt ?? ""}`;
    case "group":
      return `group-${entry.group.key}`;
  }
}

function sightingKey(sighting: VehicleDetection): string {
  return sighting.id ?? `${sighting.createdAt}-${sighting.cameraId}-${sighting.plateText}`;
}

/** "White Isuzu NPR" from the first sighting's colour, make and model, when there's no description. */
function describeAppearance(sightings: readonly VehicleDetection[]): string | null {
  const first = sightings[0];
  return first === undefined ? null : sightingAppearance(first);
}

/** "White Isuzu NPR": a sighting's colour, make and model, or `null` when none are known. */
export function sightingAppearance(sighting: VehicleDetection): string | null {
  const parts = [sighting.color, sighting.make, sighting.model].filter(isKnown);
  if (parts.length === 0) return null;
  const text = parts.join(" ");
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** Not blank, and not "unknown" or "null" (how upstream sometimes writes a missing value). */
function isKnown(value: string | null | undefined): value is string {
  if (!value) return false;
  const normalised = value.trim().toLowerCase();
  return normalised !== "" && normalised !== "unknown" && normalised !== "null";
}
